/**
 * Web app block type registry (the 6 base types). Single source of
 * truth for render/grading: player and local re-grading use the
 * SAME instance. Adding a type = registering plugin here (engine does
 * not change) — `learning-blocks` extensibility invariant.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { defaultRegistry } from "@aula/shared";

export const blockRegistry = defaultRegistry();

/** UI metadata for each block type (for authoring editor).
 * `label`/`hint` are MessageDescriptors: consumers render with `t()`. */
export interface BlockKindMeta {
  kind: string;
  label: MessageDescriptor;
  hint: MessageDescriptor;
}

export const BLOCK_KINDS: BlockKindMeta[] = [
  { kind: "quiz", label: msg`Quiz`, hint: msg`Single or multiple choice` },
  { kind: "flashcard", label: msg`Flashcard`, hint: msg`Front / back` },
  { kind: "fill-blank", label: msg`Fill in the blanks`, hint: msg`Fill the gaps` },
  { kind: "match", label: msg`Match`, hint: msg`Match the pairs` },
  { kind: "code", label: msg`Code`, hint: msg`Write or complete code` },
  { kind: "listen-type", label: msg`Listen and type`, hint: msg`Transcribe the audio` },
];

/** Default `def` template for a new type (authoring). */
export function defaultDefFor(kind: string): unknown {
  switch (kind) {
    case "quiz": {
      return {
        prompt: "",
        options: [
          { id: "a", text: "" },
          { id: "b", text: "" },
        ],
        correct: ["a"],
        multiple: false,
      };
    }
    case "flashcard": {
      return { front: "", back: "" };
    }
    case "fill-blank": {
      return { text: "", blanks: [{ id: "1", answers: [""] }] };
    }
    case "match": {
      return {
        pairs: [
          { left: "", right: "" },
          { left: "", right: "" },
        ],
      };
    }
    case "code": {
      return { prompt: "", language: "text", checks: [{ kind: "contains", value: "" }] };
    }
    case "listen-type": {
      return { audioRef: "", transcript: "" };
    }
    default: {
      return {};
    }
  }
}
